import { Search as SearchIcon } from '@mui/icons-material';
import { FilledInputProps, TextField, useTheme } from '@mui/material';
import { ClearButton } from 'components/buttons';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useSearchInputStyles from './styles';
import { SearchInputProps } from './types';

const SearchInput: React.FunctionComponent<SearchInputProps> = (props) => {
  const { value: propValue = '', onChange: propOnChange = () => ({}), onSearchInputFocus } = props;
  const { t } = useTranslation('common');
  const classes = useSearchInputStyles(props);
  const inputRef = useRef<HTMLInputElement>(null);
  const textFieldRef = useRef<HTMLDivElement>(null);

  const handleClickOutsideField = useCallback(
    (event: any) => {
      if (textFieldRef.current && !textFieldRef.current.contains(event.target)) {
        onSearchInputFocus(false);
      }
    },
    [onSearchInputFocus]
  );

  useEffect(() => {
    window.addEventListener('mousedown', handleClickOutsideField);
    return () => {
      window.removeEventListener('mousedown', handleClickOutsideField);
    };
  }, [handleClickOutsideField]);

  const [value, setValue] = useState<string>(propValue);
  useEffect(() => {
    if (propValue !== value) {
      setValue(propValue);
    }
    // only do this when the propValue has changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propValue]);

  const debouncedOnChange = debounce(propOnChange, 500);

  useEffect(() => {
    // check if mounted, cause we don't want to fire propOnChange on the initial mounting render
    if (!mountedRef.current || !inputRef.current || !propOnChange) {
      return;
    }
    const inputEvent = new Event('input', { bubbles: true });
    const event: React.ChangeEvent<HTMLInputElement> = {
      ...inputEvent,
      target: inputRef.current,
      currentTarget: inputRef.current,
      nativeEvent: inputEvent,
      isDefaultPrevented: () => false,
      isPropagationStopped: () => false,
      persist: () => null,
    };
    debouncedOnChange(event);
    // only fire when the value changes. assume the callbacks are not stale
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  }, []);

  const onClear = useCallback(() => {
    setValue('');
  }, []);

  const clearButton = useMemo(() => <ClearButton onClick={onClear} />, [onClear]);

  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
  }, []);

  const endAdornment = useMemo(
    () => (
      <>
        {!!value?.length && clearButton}
        <SearchIcon />
      </>
    ),
    [clearButton, value?.length]
  );

  const { spacing } = useTheme();

  const InputProps: Partial<FilledInputProps> = {
    classes: {
      input: classes.input,
      root: classes.root,
    },
    ...props.InputProps,
    disableUnderline: true,
    endAdornment,
    style: { borderRadius: spacing() },
    onFocus: () => onSearchInputFocus(true),
    onBlur: () => onSearchInputFocus(false),
  };

  const renderSearch = () => {
    return (
      <TextField
        margin='none'
        placeholder={t('search')}
        ref={textFieldRef}
        autoComplete='off'
        InputProps={InputProps}
        inputRef={inputRef}
        variant='filled'
        value={value}
        onChange={onChange}
        className={classes.textField}
      />
    );
  };

  return renderSearch();
};

export default SearchInput;
